import styled from "styled-components";
import { BackButton } from "../../../components/layout/back-button/back-button.component";
import { useCallback, useEffect, useRef, useState } from "react";
import { colors } from "../../../theme/colors";
import { Spacer } from "../../../components/ui/spacer/spacer.component";
import { Row } from "../../../components/ui/grid/row.component";
import {
  MdArrowBack,
  MdArrowForward,
  MdCheckCircleOutline,
  MdDelete,
} from "react-icons/md";
import { Column } from "../../../components/ui/grid/column.component";
import { wait } from "../../../utils/functions/misc/wait";
import { Subject, debounceTime } from "rxjs";
import { Button, Form, Modal, Tooltip } from "antd";
import { sluggify } from "../../../utils/functions/string/sluggify";
import {
  CUSTOM_NAME_MAX_LENGTH,
  getCustomNameValidationError,
  sanitizeCustomName,
} from "../../../utils/functions/string/custom-url";
import { useForm as useAntdForm } from "antd/es/form/Form";
import { Splash } from "../../../components/ui/splash/splash.component";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SubmitButton } from "../../../components/layout/submit-button/submit-button.component";
import { AxiosError } from "axios";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import toast from "react-hot-toast";
import {
  FilmProfile,
  Award as AwardModel,
} from "../../../models/film-profile.model";
import { axios } from "../../../api/axios";
import { Moment } from "moment";
import { Step } from "../sections/step.layout";
import { CreateAward } from "../components/create-award.component";
import { StepNav } from "../components/step-nav.component";
import { card } from "../components/section-card.component";
import { v4 } from "uuid";
import dayjs from "dayjs";
import _ from "lodash";
import { countries } from "../../../data/countries";
import { Poster } from "../../../types/Poster";
import { useAtomValue } from "jotai";
import { authAtom } from "../../../atoms/auth.atom";
import { config } from "../../../config/config";
import useModal from "antd/es/modal/useModal";
import { ResponseWithMessage } from "../../../types/ResponseWithMessage";

/** Maps API / form field keys to wizard step index (for error navigation). */
const sections = {
  title: 0,
  english_title: 0,
  custom_name: 0,
  language: 0,
  original_language: 0,
  tagline: 0,
  category: 0,
  genre: 0,
  synopsis: 0,
  duration: 0,
  date_of_completion: 0,
  shooting_format: 0,
  aspect_ratio: 0,
  keywords: 0,
  country: 0,
  language_rating: 0,
  violence_rating: 0,
  adult_content_rating: 0,
  drug_rating: 0,
  drugs_rating: 0,
  harmful_rating: 0,
  hateful_rating: 0,
};

const customUrlSubject$ = new Subject<string>();
const professionalProfileSubject$ = new Subject<string>();

export const CreateFilm = ({ editing }: { editing?: boolean }) => {
  const navigate = useNavigate();

  const { customURL: filmId } = useParams();
  const [searchParams] = useSearchParams();
  const stepFromUrl = Number(searchParams.get("step"));

  const [modal, modalConfig] = useModal();

  const [isDraft, setIsDraft] = useState(false);
  const [filmObj, setFilmObj] = useState<FilmProfile | null>(null);
  const [isDistributed, setIsDistributed] = useState(false);

  const [urlVerified, setUrlVerified] = useState<boolean | null>(null);
  const [showVerification, setShowVerification] = useState(true);
  const [customUrl, setCustomUrl] = useState("");

  const customUrlInput = useRef<HTMLInputElement | null>(null);

  const [isShowingAwardModal, setIsShowingAwardModal] = useState(false);
  const [nameModalType, setNameModalType] = useState<0 | 1 | 2 | 3 | null>(
    null
  );
  const [nameModalIndex, setNameModalIndex] = useState<number>(0);

  const [nameOptions, setNameOptions] = useState<
    {
      value: string;
      label: string;
      logo?: string;
      id?: string;
      full_name?: string;
      custom_url?: string;
      type?: "professional" | "business";
    }[]
  >([]);

  const [form] = useAntdForm<FilmProfile>();
  const [awardForm] = useAntdForm<AwardModel>();

  const onFilmTitleChange = useCallback(
    (newTitle: string) => {
      const currentCustomUrl =
        customUrl.trim() ||
        String(form.getFieldValue("custom_name") ?? "").trim();

      if (!currentCustomUrl) {
        const slug = sanitizeCustomName(
          sluggify(newTitle, "-"),
          CUSTOM_NAME_MAX_LENGTH
        );
        form.setFieldValue("custom_name", slug);
        customUrlSubject$.next(slug);
        if (customUrlInput.current) {
          customUrlInput.current.value = slug;
        }
      }

      if (!form.isFieldTouched("english_title")) {
        form.setFieldValue("english_title", newTitle);
      }
    },
    [form, customUrl]
  );

  const [completedSteps, setCompletedSteps] = useState<(null | boolean)[]>([
    null,
    null,
    null,
    null,
    null,
  ]);
  const [currentStep, setCurrentStep] = useState(
    Number.isInteger(stepFromUrl) && stepFromUrl >= 0 && stepFromUrl <= 4
      ? stepFromUrl
      : 0
  );

  const auth = useAtomValue(authAtom);

  const [verticalPoster, setVerticalPoster] = useState<Poster[]>([]);
  const [horizontalPoster, setHorizontalPoster] = useState<Poster[]>([]);
  const [productionStills, setProductionStills] = useState<Poster[]>([]);
  const [posterErrors, setPosterErrors] = useState<[boolean, boolean, boolean]>(
    [false, false, false]
  );

  const [isDistributionChecked, setIsDistributionChecked] = useState(false);
  const [isSubtitlesAvailable, setIsSubtitlesAvailable] = useState(false);

  const [isPublished, setIsPublished] = useState(false);

  const [synopsis, setSynopsis] = useState("");
  const [directorsStatement, setDirectorsStatement] = useState("");
  const [producersStatement, setProducersStatement] = useState("");
  const [otherInformation, setOtherInformation] = useState("");

  const [keywords, setKeywords] = useState<string[]>([]);

  const [directors, setDirectors] = useState<
    {
      name: string;
      user?: string;
      biography: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[]
  >([{ name: "", biography: "", id: v4() }]);
  const [producers, setProducers] = useState<
    {
      name: string;
      user?: string;
      filmography: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[]
  >([{ name: "", filmography: "", id: v4() }]);
  const [cast, setCast] = useState<
    {
      name: string;
      user?: string;
      character: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[]
  >([]);
  const [crew, setCrew] = useState<
    {
      name: string;
      user?: string;
      role: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[]
  >([]);

  const [awards, setAwards] = useState<AwardModel[]>([]);
  const [awardEditingIndex, setAwardEditingIndex] = useState<number | null>(
    null
  );

  const [searchQuery, setSearchQuery] = useState("");

  const [subtitles, setSubtitles] = useState<
    { language: string; file: string }[]
  >([]);

  const [showSuccess, setShowSuccess] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const onSubmit = useCallback(
    async (editing = false, draft = false) => {
      try {
        if (!draft && !horizontalPoster.length)
          return toast.error("Please upload horizontal posters!");
        if (!draft && !verticalPoster.length)
          return toast.error("Please upload vertical posters!");
        // if (!draft && !productionStills.length)
        //   return toast.error("Please upload production stills!");

        if (!draft && !synopsis)
          return form.setFields([
            { name: "synopsis", errors: ["Please enter a synopsis!"] },
          ]);
        else {
          form.setFields([{ name: "synopsis", errors: [] }]);
        }

        if (urlVerified === false) {
          form.setFields([
            {
              name: "custom_name",
              errors: [
                "This custom URL is taken or invalid. Choose a different short name (not a link).",
              ],
            },
          ]);
          setCurrentStep(0);
          return toast.error(
            "Please choose a different custom URL — use a short name, not a link."
          );
        }

        !draft && (await form.validateFields());

        const profile: FilmProfile = {
          ...form.getFieldsValue(true),
        };
        if (profile.duration) {
          (profile as any).duration = profile.duration
            ? typeof profile.duration === "string"
              ? profile.duration
              : `${profile.duration.mm}:${profile.duration.ss}`
            : null;
        }
        profile.keywords = keywords ?? [];
        profile.synopsis = synopsis;
        if (directorsStatement) profile.director_statement = directorsStatement;
        if (producersStatement) profile.producer_statement = producersStatement;
        if (otherInformation) profile.other_information = otherInformation;

        const validDirectors = directors.filter((el) => !!el.name);
        const validProducers = producers.filter((el) => !!el.name);

        if (!draft && validDirectors.length === 0)
          return toast.error("Please add at least one director!");
        if (!draft && validProducers.length === 0)
          return toast.error("Please add at least one producer!");

        const invalidCast = cast.filter((el) => el.name && !el.character);
        const invalidCrew = crew.filter((el) => el.name && !el.role);

        if (invalidCast.length)
          return toast.error("Please ensure all cast have a character added!");
        if (invalidCrew.length)
          return toast.error("Please ensure all crew have a role selected!");

        profile.cast = cast
          .filter((el) => el.name && el.character)
          .map((el) => ({
            name: el.name,
            character: el.character,
            role: el.character,
            ...(el.user ? { profile_id: el.user } : {}),
            ...(el.id ? { id: el.id! } : {}),
            profile_type: el.type,
          }));
        profile.crew = crew
          .filter((el) => el.name && el.role)
          .map((el) => ({
            name: el.name,
            role: el.role,
            ...(el.user ? { profile_id: el.user } : {}),
            ...(el.id ? { id: el.id! } : {}),
            profile_type: el.type,
            biography: "",
          }));
        profile.crew = [
          ...profile.crew,
          ...directors
            .filter((el) => el.name)
            .map((el) => ({
              name: el.name,
              biography: el.biography,
              role: "director",
              ...(el.user ? { profile_id: el.user! } : {}),
              ...(el.id ? { id: el.id! } : {}),
              profile_type: el.type,
            })),
        ] as any;
        profile.crew = [
          ...profile.crew,
          ...producers
            .filter((el) => el.name)
            .map((el) => ({
              name: el.name,
              filmography: el.filmography,
              role: "producer",
              ...(el.user ? { profile_id: el.user! } : {}),
              ...(el.id ? { id: el.id! } : {}),
              profile_type: el.type,
            })),
        ] as any;
        profile.country = profile.country
          ?.split("-")
          ?.slice(1)
          ?.join("-")
          ?.toLowerCase();
        profile.date_of_completion = (
          profile.date_of_completion as Moment
        )?.format("DD-MM-YYYY");
        profile.awards = awards.map((award) => ({
          ...award,
          award_year: +dayjs(award.award_year).format("YYYY"),
          country: award.country.toLowerCase(),
        }));
        delete (profile as any).images;
        delete (profile as any).film_image;

        const newProfileForm = new FormData();
        for (const key in profile) {
          if (profile[key as any as keyof typeof profile] === undefined)
            continue;

          const value = profile[key as keyof typeof profile]!;
          if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
              newProfileForm.append(
                key,
                (typeof value[i] === "string"
                  ? value[i]
                  : JSON.stringify(value[i])) as any
              );
            }
          } else {
            newProfileForm.append(key, value as string);
          }
        }
        newProfileForm.append("status", draft ? "draft" : "published");

        console.log("verticalPoster");
        console.log(verticalPoster);
        const images = [
          ...verticalPoster.map((poster, index) => ({
            media_type: "poster",
            aspect_ratio:
              poster.type === 0
                ? "3_4"
                : poster.type === 1
                  ? "4_5"
                  : poster.type === 2
                    ? "1_1"
                    : "vertical_video",
            tags: ["vertical-poster", `vertical-poster-${index}`],
            image: poster?.file ?? poster.url,
            id: poster.id,
          })),
          ...horizontalPoster.map((poster, index) => ({
            media_type: "poster",
            aspect_ratio:
              poster.type === 0
                ? "16_9"
                : poster.type === 1
                  ? "4_3"
                  : poster.type === 2
                    ? "2.39_1"
                    : "16_9",
            tags: ["horizontal-poster", `horizontal-poster-${index}`],
            image: poster?.file ?? poster.url,
            id: poster.id,
          })),
          ...productionStills.map((poster, index) => ({
            media_type: "still",
            aspect_ratio: poster.aspectRatio ?? "1_1",
            tags: ["production-stills", `production-stills-${index}`],
            image: poster?.file ?? poster.url,
            id: poster.id,
          })),
        ];
        console.log(images);
        for (const image of images) {
          if (
            typeof image.image! === "object" &&
            "media_type" in (image.image ?? {})
          )
            continue;

          const name =
            image.image instanceof File
              ? `${v4()}.${image.image.name.split(".").at(-1)}`
              : null;
          if (image.image instanceof File && name)
            image.image = new File([image.image], name, {
              type: image.image.type,
            });
          newProfileForm.append("images", image.image!);
          newProfileForm.append(
            "image_metadata",
            JSON.stringify({
              media_type: image.media_type,
              aspect_ratio: image.aspect_ratio,
              tags: image.tags,
              id: image.id,
              name,
            })
          );
        }

        (editing ? axios.put : axios.post)<
          ResponseWithMessage<{ film: FilmProfile }>
        >("/client/film", newProfileForm, {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(draft || editing ? { "X-FILM-ID": filmId } : {}),
          },
        })
          .then((resp) =>
            handleResponse(resp, async (res) => {
              setFilmObj(res.data.film);
              if (draft && editing) return;
              else if (draft && !editing)
                return navigate(
                  `/f/${res.data.film.custom_name}/edit?draft=true&step=${
                    currentStep + 1
                  }`
                );

              setShowSuccess(true);
              await wait(3000);

              if (!resp.data.film.is_distributed)
                modal.confirm({
                  title: "Submit for distribution",
                  content:
                    "Do you want to submit this film for distribution? If you choose not to submit it now, you will sitll have the option of doing so later.",
                  okText: "Submit for distribution",
                  cancelText: "Later",
                  onOk: async () => {
                    navigate(`/f/${res.data.film.custom_name}/distribute`, {
                      replace: true,
                    });
                  },
                  onCancel: () => {
                    navigate(`/f/${res.data.film.custom_name}`, {
                      replace: true,
                    });
                  },
                });
              else
                navigate(`/f/${res.data.film.custom_name}`, { replace: true });
            })
          )
          .catch((error) => {
            console.log(error);

            if (error instanceof AxiosError) {
              const responseData = error.response?.data ?? {};

              // Handle string message
              if (typeof responseData.message === "string") {
                return toast.error(responseData.message);
              }

              // Extract error fields - handle both direct errors and errors wrapped in "message"
              const errorFields =
                typeof responseData.message === "object"
                  ? responseData.message
                  : responseData;

              // Helper function to flatten nested errors for array fields
              // e.g., { "0": ["error"], "1": ["error"] } -> ["error", "error"]
              const flattenErrors = (errors: unknown): string[] => {
                if (Array.isArray(errors)) {
                  return errors.map(String);
                }
                if (typeof errors === "object" && errors !== null) {
                  return Object.values(errors).flatMap((e) => flattenErrors(e));
                }
                return [String(errors)];
              };

              const unhandledErrors: string[] = [];
              for (const key in errorFields) {
                const fieldErrors = flattenErrors(errorFields[key]);

                // Check if field is a known form field (exists in sections map)
                const isKnownField = key in sections;

                if (isKnownField) {
                  // Always try to set field errors - Antd form.setFields is safe
                  form.setFields([
                    {
                      name: key,
                      errors: fieldErrors,
                    },
                  ]);

                  // Navigate to the step containing this field
                  const stepIndex = sections[key as keyof typeof sections];
                  if (stepIndex !== undefined && stepIndex !== currentStep) {
                    setCurrentStep(stepIndex);
                  }
                } else {
                  // Field not in form, add to unhandled errors for toast
                  unhandledErrors.push(`${key}: ${fieldErrors.join(", ")}`);
                }
              }

              // Show toast for any unhandled errors
              if (unhandledErrors.length > 0) {
                toast.error(unhandledErrors.join("; "));
              }
            } else {
              toast.error("There was an error");
            }
          });
      } catch (e) {
        if (e instanceof AxiosError) {
          handleResponse(e);
        } else {
          console.log(e);
          toast.error("Please fix the errors in the form!");
        }
      }
    },
    [
      form,
      directors,
      producers,
      cast,
      crew,
      awards,
      horizontalPoster,
      verticalPoster,
      productionStills,
      filmId,
      urlVerified,
      synopsis,
      directorsStatement,
      producersStatement,
      otherInformation,
      keywords,
      currentStep,
      setCurrentStep,
    ]
  );

  const isStepValid = useCallback(
    (step: number, data?: { title?: string }) => {
      const fieldHasAntdError = (name: Parameters<typeof form.getFieldError>[0]) =>
        (form.getFieldError(name)?.length ?? 0) > 0;

      if (step === 0) {
        const titleVal = form.getFieldValue("title") ?? data?.title;
        const dur = form.getFieldValue("duration");
        const genre = form.getFieldValue("genre");
        const ratings = [
          "language_rating",
          "violence_rating",
          "hateful_rating",
          "adult_content_rating",
          "drug_rating",
          "harmful_rating",
        ] as const;
        const ratingValuesOk = ratings.every((r) => {
          const v = form.getFieldValue(r);
          return v !== undefined && v !== null && v !== "";
        });

        const valuesOk =
          !!String(titleVal ?? "").trim() &&
          !!String(form.getFieldValue("english_title") ?? "").trim() &&
          !!String(form.getFieldValue("custom_name") ?? "").trim() &&
          urlVerified !== false &&
          !!form.getFieldValue("language") &&
          !!form.getFieldValue("category") &&
          !!String(form.getFieldValue("tagline") ?? "").trim() &&
          (Array.isArray(genre) ? genre.length > 0 : !!genre) &&
          !!synopsis?.trim() &&
          dur != null &&
          String(dur.mm ?? "").length > 0 &&
          String(dur.ss ?? "").length > 0 &&
          !!form.getFieldValue("date_of_completion") &&
          !!form.getFieldValue("country") &&
          !!form.getFieldValue("shooting_format") &&
          !!form.getFieldValue("aspect_ratio") &&
          ratingValuesOk;

        const stepZeroNames: Parameters<typeof form.getFieldError>[0][] = [
          "title",
          "english_title",
          "custom_name",
          "language",
          "category",
          "tagline",
          "genre",
          "synopsis",
          "duration",
          "date_of_completion",
          "country",
          "shooting_format",
          "aspect_ratio",
          ...ratings,
        ];
        const noAntdErrors = stepZeroNames.every((n) => !fieldHasAntdError(n));

        return valuesOk && noAntdErrors;
      }

      if (step === 1) {
        return (
          !!directors.filter((director) => !!director.name).length &&
          !!producers.filter((producer) => !!producer.name).length &&
          !cast.filter((c) => !!c.name && !c.character).length &&
          !crew.filter((c) => !!c.name && !c.role).length
        );
      }

      if (step === 2) {
        return true;
      }

      if (step === 3) {
        return (
          verticalPoster.length > 0 &&
          horizontalPoster.length > 0 &&
          !posterErrors[0] &&
          !posterErrors[1]
        );
      }

      if (step === 4) {
        return true;
      }

      return true;
    },
    [
      form,
      synopsis,
      urlVerified,
      verticalPoster,
      horizontalPoster,
      posterErrors,
      cast,
      crew,
      directors,
      producers,
    ]
  );

  const refreshStepCompletion = useCallback(() => {
    setCompletedSteps(() => {
      const next: (boolean | null)[] = [null, null, null, null, null];
      for (let i = 0; i < 4; i++) {
        next[i] = isStepValid(i);
      }
      next[4] = next.slice(0, 4).every((s) => s === true);
      return next;
    });
  }, [isStepValid]);

  useEffect(() => {
    if (editing && !loaded) return;
    refreshStepCompletion();
  }, [
    synopsis,
    urlVerified,
    verticalPoster,
    horizontalPoster,
    posterErrors,
    directors,
    producers,
    cast,
    crew,
    editing,
    loaded,
    refreshStepCompletion,
  ]);

  useEffect(() => {
    if (editing && !loaded) {
      axios
        .get<FilmProfile>("/client/film", {
          headers: {
            "X-FILM-ID": filmId,
          },
        })
        .then((response) => {
          const film = { ...response.data };
          film.custom_name = film.custom_name
            ? sanitizeCustomName(film.custom_name, CUSTOM_NAME_MAX_LENGTH)
            : film.custom_name;
          setFilmObj(film);
          if (film.custom_name) setCustomUrl(film.custom_name);
          setIsDraft(film.status === "draft");
          setIsDistributed(film.is_distributed);

          for (const pendingChange of film.pending_changes) {
            film[pendingChange.field_name as any as keyof typeof film] =
              pendingChange.proposed_value as any as never;
          }

          if (film.date_of_completion) {
            const raw = String(film.date_of_completion);
            const iso = raw.slice(0, 10);
            film.date_of_completion = /^\d{4}-\d{2}-\d{2}$/.test(iso)
              ? dayjs(iso)
              : dayjs(raw, "DD-MM-YYYY");
          }
          if (film.keywords) setKeywords(film.keywords);
          if (film.country) {
            film.country =
              countries.find(
                ({ value }) =>
                  value.split("-")[1].toLowerCase() ===
                  film.country?.toLowerCase()
              )?.value ?? film.country;
          }
          if (film.synopsis) setSynopsis(film.synopsis);

          film.duration = {
            mm: (film.duration as string)?.split(":")[1] ?? 0,
            ss: (film.duration as string)?.split(":")[2] ?? 0,
          };
          delete film["self_censor"];
          for (const key in film) {
            if ((film as any)[key] === null) delete (film as any)[key];
          }
          if (film.film_image) {
            const verticalPosters = film.film_image.filter(
              (image) =>
                image.tags?.includes("vertical-poster") ||
                image.aspect_ratio === "3_4" ||
                image.aspect_ratio === "4_5" ||
                (image.aspect_ratio === "1_1" && image.media_type === "poster")
            );
            const horizontalPosters = film.film_image.filter(
              (image) =>
                image.tags?.includes("horizontal-poster") ||
                image.aspect_ratio === "16_9" ||
                image.aspect_ratio === "4_3" ||
                image.aspect_ratio === "2_39_1"
            );
            const productionStills = film.film_image.filter(
              (image) =>
                image.tags?.includes("production-stills") ||
                image.media_type === "still"
            );

            setVerticalPoster(
              verticalPosters.map((poster) => ({
                url: poster.image,
                file: null,
                id: poster.id,
                type:
                  poster.aspect_ratio === "3_4"
                    ? 0
                    : poster.aspect_ratio === "4_5"
                      ? 1
                      : 2,
              }))
            );

            setHorizontalPoster(
              horizontalPosters.map((poster) => ({
                url: poster.image,
                file: null,
                id: poster.id,
                type:
                  poster.aspect_ratio === "16_9"
                    ? 0
                    : poster.aspect_ratio === "4_3"
                      ? 1
                      : 2,
              }))
            );

            setProductionStills(
              productionStills.map((poster) => ({
                url: poster.image,
                file: null,
                id: poster.id,
              }))
            );
          }
          if (film.film_cast) {
            setCast(
              film.film_cast.map((cast) => ({
                id: cast.id,
                name: cast.name,
                character: cast.character,
                user: cast.user ?? cast.profile_id,
                logo:
                  !cast.profile_url || cast.profile_url === "none"
                    ? "/images/ui/avatar.png"
                    : cast.profile_url,
              }))
            );
          }
          let directors: {
            id: string;
            name: string;
            biography: string;
            user: string;
            logo: string;
          }[] = [];
          let producers: {
            id: string;
            name: string;
            filmography: string;
            user: string;
            logo: string;
          }[] = [];
          if (film.film_crew) {
            directors = film.film_crew
              .filter((crew) => crew.role === "director")
              .map((director) => ({
                id: director.id,
                name: director.name,
                biography: director.biography!,
                user: director.user ?? director.profile_id,
                logo:
                  !director.profile_url || director.profile_url === "none"
                    ? "/images/ui/avatar.png"
                    : director.profile_url,
              }));
            if (directors.length === 0) {
              directors.push({
                name: "",
                biography: "",
                id: v4(),
                user: "",
                logo: "/images/ui/avatar.png",
              });
            }
            producers = film.film_crew
              .filter((crew) => crew.role === "producer")
              .map((producer) => ({
                id: producer.id,
                name: producer.name,
                filmography: producer.filmography!,
                user: producer.user ?? producer.profile_id,
                logo:
                  !producer.profile_url || producer.profile_url === "none"
                    ? "/images/ui/avatar.png"
                    : producer.profile_url,
              }));
            if (producers.length === 0) {
              producers.push({
                name: "",
                filmography: "",
                id: v4(),
                user: "",
                logo: "/images/ui/avatar.png",
              });
            }
            const crew = film.film_crew
              .filter(
                (crew) => crew.role !== "director" && crew.role !== "producer"
              )
              .map((crew) => ({
                id: crew.id,
                name: crew.name,
                role: crew.role,
                user: crew.user ?? crew.profile_id,
                logo:
                  !crew.profile_url || crew.profile_url === "none"
                    ? "/images/ui/avatar.png"
                    : crew.profile_url,
              }));
            console.log(crew);

            setDirectors(
              directors.length
                ? directors
                : [{ name: "", biography: "", id: v4() }]
            );
            setProducers(
              producers.length
                ? producers
                : [{ name: "", filmography: "", id: v4() }]
            );
            setCrew(crew);
          }
          if (film.film_award) {
            const newAwards = _.orderBy(
              film.film_award,
              "award_year",
              "desc"
            ).map((award) => ({
              ...award,
              award_year: dayjs(award.award_year.toString(), "YYYY"),
              award_winner: award.award_winner_name,
            }));
            setAwards(newAwards);
          }

          if (customUrlInput.current) {
            customUrlInput.current.value = film.custom_name;
          }

          const pendingChanges = film.pending_changes;
          delete (film as any)["pending_changes"];
          form.setFieldsValue(film);
          for (const pendingChange of pendingChanges) {
            form.setFields([
              {
                name: pendingChange.field_name,
                warnings: [
                  "This change is waiting for approval and will reflect once approved!",
                ],
              },
            ]);
          }
          for (const cast of film.film_cast ?? []) {
            form.setFieldValue(`character.${cast.id}`, cast.character);
          }
          for (const crew of film.film_crew ?? []) {
            if (crew.role !== "director" && crew.role !== "producer")
              form.setFieldValue(`role.${crew.id}`, crew.role);
          }

          if (film.status === "published") setIsPublished(true);

          setLoaded(() => true);
        })
        .catch(handleResponse);
    }
  }, [form, editing, filmId, customUrlInput, loaded]);

  useEffect(() => {
    if (
      auth.loaded &&
      auth.user?.phone_code &&
      auth.user.phone_number &&
      !auth.user.phone_verified
    ) {
      wait(500).then(() =>
        window.location.replace(
          `${config.auth_url}/verify?source=distribute&r=${config.base_url}/f/create`
        )
      );
    }
  }, [auth]);

  useEffect(() => {
    const listener = customUrlSubject$
      .pipe(debounceTime(500))
      .subscribe((value) => {
        setCustomUrl(value);
      });

    return () => listener.unsubscribe();
  }, []);

  useEffect(() => {
    if (!customUrl) return;

    if (getCustomNameValidationError(customUrl)) {
      setUrlVerified(false);
      setShowVerification(true);
      return;
    }

    setShowVerification(false);

    const excludeId = filmObj?.id || filmId;
    axios
      .get(
        `/client/validate/custom-url?q=${encodeURIComponent(customUrl)}&category=films` +
        (excludeId ? `&id=${encodeURIComponent(excludeId)}` : "")
      )
      .then(() => {
        setUrlVerified(true);
      })
      .catch(() => {
        setUrlVerified(false);
      })
      .finally(() => {
        setShowVerification(true);
      });
  }, [customUrl, filmId, filmObj?.id]);

  useEffect(() => {
    const listener = professionalProfileSubject$
      .pipe(debounceTime(500))
      .subscribe((query) => {
        axios
          .get<
            {
              full_name: string;
              business_name?: string;
              custom_url: string;
              id: string;
              logo: string;
              profile_type?: "professional" | "business";
            }[]
          >(
            `/client/profile/search?q=${query}&type=${nameModalType === 1 || nameModalType === 3
              ? "all"
              : "professional"
            }`
          )
          .then((response) => {
            setNameOptions(
              response.data.map((res) => ({
                value: res.full_name ?? res.business_name,
                label: (
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <img
                      src={res.logo ?? "/images/ui/avatar.png"}
                      alt="Profile Picture"
                      style={{
                        width: "2rem",
                        height: "2rem",
                        borderRadius: "50%",
                      }}
                    />
                    {res.full_name ?? res.business_name}
                    {/* <a href={res.custom_url} target="_blank">
                      See profile
                    </a> */}
                  </div>
                ) as any,
                ...res,
                full_name: res.full_name ?? res.business_name,
                type: res.profile_type,
              }))
            );
          });
      });

    return () => listener.unsubscribe();
  }, [nameModalType]);

  const onStepChange = useCallback(
    (newStep: number) => {
      // if (newStep < currentStep) {
      //   setIsCurrentError(false);
      //   setCurrentStep(newStep);
      //   return;
      // }

      const isValid = isStepValid(currentStep);
      const newCompletedSteps = [...completedSteps];
      newCompletedSteps[currentStep] = isValid;

      setCompletedSteps(newCompletedSteps);
      // setIsCurrentError(false);
      setCurrentStep(newStep);
    },
    [currentStep, form, completedSteps, isStepValid]
  );

  const onKeyDown = useCallback(
    (evt: KeyboardEvent) => {
      if ((evt.ctrlKey || evt.metaKey) && evt.key === "Enter") {
        const newStep = Math.min(
          4,
          Math.max(0, currentStep + (evt.shiftKey ? -1 : 1))
        ); // Ensures it doesn't go before the first step or past the last step

        onStepChange(newStep);
      }
    },
    [onStepChange]
  );

  useEffect(() => {
    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  return (
    <Main>
      {modalConfig}
      <ProgressBar $progress={currentStep / 5}></ProgressBar>
      <Splash
        show={showSuccess}
        title="Film created successfully!"
        icon={<MdCheckCircleOutline />}
      />
      <Modal
        open={isShowingAwardModal}
        title={awardEditingIndex === null ? "Add new award" : "Edit award"}
        okText={awardEditingIndex === null ? "Add award" : "Save changes"}
        cancelText="Cancel"
        width={760}
        destroyOnClose
        centered
        onOk={async () => {
          try {
            await awardForm.validateFields();
            awardForm.submit();

            const hasErrors =
              awardForm.getFieldsError().filter((el) => el.errors.length)
                .length > 0;
            if (hasErrors) return;

            const value = awardForm.getFieldsValue();

            value.award_winner_type = (value.award_winner as string)?.split(
              "__"
            )[1] as "director" | "producer" | "cast" | "crew";
            value.award_winner_value = value.award_winner;
            value.award_winner = +(value.award_winner as string)?.split(
              "__"
            )[0];
            const array =
              value.award_winner_type === "director"
                ? directors
                : value.award_winner_type === "producer"
                  ? producers
                  : value.award_winner_type === "cast"
                    ? cast
                    : crew;
            console.log(array, value.award_winner);
            if (array[value.award_winner]?.id) {
              if (value.award_winner_type === "cast")
                value.award_winner_cast = array[value.award_winner]?.id;
              else value.award_winner_crew = array[value.award_winner]?.id;
            }
            if (array[value.award_winner].type) {
              value.award_winner_type = array[value.award_winner].type!;
            }
            value.award_winner_name = array[value.award_winner]?.name;
            value.award_year = dayjs(value.award_year);

            value.country =
              value.country?.split("-")?.slice(1)?.join("-") ?? "";

            value.award_winner = value.award_winner_value;

            if (awardEditingIndex === null) {
              setAwards([...awards, value]);
            } else {
              const newAwards = [...awards];
              newAwards[awardEditingIndex] = value;

              setAwards(newAwards);
              setAwardEditingIndex(null);
            }

            awardForm.resetFields();

            setIsShowingAwardModal(false);
          } catch (e) {
            toast.error("Fix the errors in the form!");
          }
        }}
        onCancel={() => {
          awardForm.resetFields();
          setIsShowingAwardModal(false);
        }}
      >
        <Form form={awardForm}>
          <CreateAward
            {...{ countryOptions: countries, directors, producers, cast, crew }}
          />
        </Form>
      </Modal>
      <BackButton />
      <Form form={form} onValuesChange={refreshStepCompletion}>
        <FormRow>
          <Sidebar>
            <StepNav
              current={currentStep}
              completed={completedSteps}
              onChange={onStepChange}
              items={[
                {
                  title: "Film Details",
                  description: "Basic, technical and censor information.",
                },
                {
                  title: "Cast and Crew Details",
                  description: "Directors, producers, cast and crew.",
                },
                {
                  title: "Awards",
                  description: "Optional",
                },
                {
                  title: "Posters and Other Details",
                  description: "Artwork, statements and additional info.",
                },
                {
                  title: "Review",
                  description: "Confirm and submit your film.",
                },
              ]}
            />
            <NavCard>
              <NavRow>
                <Tooltip
                  title={currentStep > 0 ? "Ctrl + Shift + Enter" : ""}
                >
                  <NavButton
                    type="default"
                    icon={<MdArrowBack />}
                    disabled={currentStep === 0}
                    onClick={() => onStepChange(currentStep - 1)}
                  >
                    Previous
                  </NavButton>
                </Tooltip>
                {currentStep !== 4 ? (
                  <Tooltip title="Ctrl + Enter">
                    <NavButton
                      type="primary"
                      onClick={() => {
                        onStepChange(currentStep + 1);

                        if (
                          (!filmId || isDraft) &&
                          form.getFieldValue("title") &&
                          customUrl &&
                          form.getFieldValue("english_title")
                        ) {
                          onSubmit(!!filmId, true);
                        }
                      }}
                    >
                      Next
                      <MdArrowForward />
                    </NavButton>
                  </Tooltip>
                ) : null}
              </NavRow>
              {editing && !isDistributed ? (
                <DeleteButton
                  type="text"
                  icon={<MdDelete />}
                  onClick={() => {
                    modal.confirm({
                      title: "Are you sure you want to delete?",
                      content:
                        "Are you sure you want to permanently delete this film? This cannot be reversed.",
                      okText: "Delete",
                      cancelText: "Cancel",
                      onOk: () => {
                        axios
                          .delete("/client/film", {
                            data: {
                              film_id: filmId,
                            },
                          })
                          .then((resp) => {
                            toast.success("Film deleted successfully!");
                            navigate("/");
                          })
                          .catch(handleResponse);
                      },
                      onCancel: () => null,
                    });
                  }}
                >
                  Delete film
                </DeleteButton>
              ) : null}
            </NavCard>
          </Sidebar>
          <Column style={{ flex: 1, minWidth: 0, alignSelf: "stretch" }}>
            <Step
              {...{
                onFilmTitleChange,
                titleFieldsAutoFocus: !filmId,
                keywords,
                loaded,
                setKeywords,
                posterErrors,
                setPosterErrors,
                form,
                customUrlInputRef: customUrlInput,
                customUrlSubject$,
                customUrl,
                urlVerified,
                showVerification,
                searchQuery,
                nameModalIndex,
                professionalProfileSubject$,
                nameOptions,
                setSearchQuery,
                step: currentStep,
                awardForm,
                setIsShowingAwardModal,
                verticalPoster,
                setVerticalPoster,
                horizontalPoster,
                setHorizontalPoster,
                productionStills,
                setProductionStills,
                isDistributionChecked,
                setIsDistributionChecked,
                isSubtitlesAvailable,
                setIsSubtitlesAvailable,
                directors,
                setDirectors,
                producers,
                setProducers,
                cast,
                setCast,
                crew,
                setCrew,
                setAwardEditingIndex,
                awards,
                setAwards,
                subtitles,
                setSubtitles,
                nameModalType,
                setNameModalType,
                setNameModalIndex,
                completedSteps,
                setCurrentStep,
                synopsis,
                setSynopsis,
                directorsStatement,
                setDirectorsStatement,
                producersStatement,
                setProducersStatement,
                otherInformation,
                setOtherInformation,
              }}
            />
          </Column>
        </FormRow>
        <Spacer direction="bottom" size={30} />
      </Form>
      {currentStep === 4 ? (
        <SubmitButton
          label={!!filmId ? "Save" : "Create"}
          onClick={() => {
            if (
              completedSteps
                .slice(0, -1)
                .filter((el) => el === false && el !== null).length
            ) {
              return toast.error(
                "Please review the errors in the form before proceeding!"
              );
            }
            onSubmit(!!filmId, false);
          }}
        />
      ) : null}
    </Main>
  );
};

const Main = styled.div`
  display: flex;
  flex-direction: column;
  padding: clamp(1.6rem, 4vw, 4.5rem);
  padding-top: 8.5rem;
  padding-right: 8rem;
  width: 100%;
  max-width: 100%;
  min-height: 100vh;

  background-color: ${colors.lightBackground.hex};

  .ant-form-item-has-error {
    margin-bottom: 9rem;
  }
  .ant-form-item-has-warning {
    margin-bottom: 9rem;
  }

  .ant-table-cell {
    vertical-align: top;
  }

  .table-striped-rows tr:nth-child(2n) td {
    background-color: ${colors.lightBackground.hex};
  }
  .table-striped-rows thead {
    background-color: #f1f1f1;
  }
`;

const FormRow = styled(Row)`
  width: 100%;
  gap: 2rem;
  flex-wrap: wrap;
  align-items: flex-start;
  flex-direction: row;

  margin-bottom: -1rem;

  @media only screen and (max-width: 48em) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div`
  flex: 0 0 28rem;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2rem;
  margin: 0;
  padding: 0;

  @media only screen and (max-width: 48em) {
    flex: 1 1 100%;
    width: 100%;
  }
`;

const NavCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.4rem;
  background-color: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 1.2rem;
  box-shadow: ${card.shadow};
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
`;

const NavButton = styled(Button)`
  flex: 1 1 0;
  min-width: 0;
  height: 4rem;
  border-radius: 0.8rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &.ant-btn-primary {
    flex-grow: 1.4;
  }

  & svg {
    font-size: 1.6rem;
  }
`;

const DeleteButton = styled(Button)`
  height: 3.6rem;
  border-radius: 0.8rem;
  color: ${colors.error.hex};
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding-inline: 1rem;

  &:hover {
    background: rgba(239, 35, 60, 0.08) !important;
    color: ${colors.error.hex} !important;
  }

  & svg {
    font-size: 1.6rem;
  }
`;

const ProgressBar = styled.div<{
  $progress: number;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 0.5rem;
  background-color: ${colors.grey.hex};
  z-index: 3;

  &::after {
    /* content: "${({ $progress }) => Math.round($progress * 100)}%"; */
    content: "";
    text-align: center;
    vertical-align: center;
    color: ${colors.white.hex};
    font-size: 1.1rem;
    font-weight: 600;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ $progress }) => Math.round($progress * 100)}%;
    background-image: linear-gradient(-90deg, #438dc7 0%, #205496 100%);
    transition: all 0.5s;
  }
`;
